import { useState, type FormEvent } from 'react'
import { Eye, EyeOff, Edit2, Trash2 } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { adminApi } from '@/utils/api'

export default function SecuritySettings() {
  const { user } = useAuth()
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Validation state
  const hasMinLength = newPassword.length >= 8
  const hasUppercase = /[A-Z]/.test(newPassword)
  const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)

  const allRequirementsMet = hasMinLength && hasUppercase && hasSymbol

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault()
    if (!user?.id) {
      setError('Missing user id for password update.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.')
      return
    }
    if (!allRequirementsMet) {
      setError('Please meet the password requirements.')
      return
    }

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      await adminApi.updateUser(user.id, { password: newPassword })
      setSuccess('Password updated successfully.')
      setOldPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setError((err as Error)?.message || 'Unable to update password.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='space-y-6 w-full'>
      {/* Password Change Section */}
      <div className='bg-white rounded-2xl p-6 md:p-8 border border-gray-200'>
        {/* Requirements Banner */}
        <div className='mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4 text-start'>
          <p className='text-sm font-semibold text-orange-600 mb-3'>
            Password Requirements
          </p>
          <ul className='space-y-2'>
            <li className='flex items-center gap-2 text-xs'>
              <span className={`h-4 w-4 rounded-full flex items-center justify-center ${
                hasMinLength ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'
              }`}>
                {hasMinLength ? '✓' : '○'}
              </span>
              <span className={hasMinLength ? 'text-green-700 font-medium' : 'text-orange-600'}>
                Minimum 8 characters long
              </span>
            </li>
            <li className='flex items-center gap-2 text-xs'>
              <span className={`h-4 w-4 rounded-full flex items-center justify-center ${
                hasUppercase ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'
              }`}>
                {hasUppercase ? '✓' : '○'}
              </span>
              <span className={hasUppercase ? 'text-green-700 font-medium' : 'text-orange-600'}>
                At least one uppercase letter (A-Z)
              </span>
            </li>
            <li className='flex items-center gap-2 text-xs'>
              <span className={`h-4 w-4 rounded-full flex items-center justify-center ${
                hasSymbol ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'
              }`}>
                {hasSymbol ? '✓' : '○'}
              </span>
              <span className={hasSymbol ? 'text-green-700 font-medium' : 'text-orange-600'}>
                At least one symbol (!@#$%^&*(),.?":{}|&lt;&gt;)
              </span>
            </li>
          </ul>
          {newPassword && !allRequirementsMet && (
            <p className='text-xs text-orange-700 mt-3 font-medium'>
              ⚠️ Please ensure all requirements above are met before submitting
            </p>
          )}
        </div>

        <form onSubmit={handleChangePassword}>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6'>
            {/* Old Password */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>
                Old Password
              </label>
              <div className='relative'>
                <input
                  type={showOldPassword ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder='Enter old password'
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'
                />
                <button
                  type='button'
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                >
                  {showOldPassword ? (
                    <EyeOff className='h-5 w-5' />
                  ) : (
                    <Eye className='h-5 w-5' />
                  )}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>
                New Password
              </label>
              <div className='relative'>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder='Enter new password'
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'
                />
                <button
                  type='button'
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                >
                  {showNewPassword ? (
                    <EyeOff className='h-5 w-5' />
                  ) : (
                    <Eye className='h-5 w-5' />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2 text-start'>
                Confirm New Password
              </label>
              <div className='relative'>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder='Confirm new password'
                  className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'
                />
                <button
                  type='button'
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                >
                  {showConfirmPassword ? (
                    <EyeOff className='h-5 w-5' />
                  ) : (
                    <Eye className='h-5 w-5' />
                  )}
                </button>
              </div>
            </div>
          </div>

          {(error || success) && (
            <div
              className={`mb-4 text-sm rounded-lg px-4 py-3 ${
                error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
              }`}
            >
              {error || success}
            </div>
          )}

          <div className='flex flex-row w-full justify-start'>
            <button
              type='submit'
              disabled={!allRequirementsMet || newPassword !== confirmPassword || saving}
              className='px-6 py-2.5 bg-gray-900 text-white rounded-lg font-semibold text-sm hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed'
            >
              {saving ? 'Updating...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>

      {/* Two-Factor Authentication Section */}
      <div className='bg-white rounded-2xl p-6 md:p-8 border border-gray-200'>
        <div className='flex items-start gap-4 mb-6'>
          <div className='h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0'>
            <svg
              className='h-6 w-6 text-gray-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
              />
            </svg>
          </div>
          <div className='flex-1'>
            <div className='flex items-center gap-2 mb-1'>
              <h3 className='text-lg font-bold text-gray-900'>Two-steps verification</h3>
              <span className='inline-flex items-center px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold'>
                ✓ Verified
              </span>
            </div>
            <p className='text-sm text-gray-600 text-start'>
              Keep your account secure with authentication step.
            </p>
          </div>
        </div>

        {/* SMS Section */}
        <div className='bg-gray-50 rounded-lg p-4 mb-4'>
          <div className='flex items-center justify-between mb-3'>
            <div>
              <h4 className='text-sm font-semibold text-gray-900'>SMS</h4>
              <p className='text-sm text-gray-600'>+1(968) 945-8832</p>
            </div>
            <div className='flex items-center gap-2'>
              <button className='p-2 hover:bg-gray-200 rounded-lg transition-colors'>
                <Edit2 className='h-4 w-4 text-gray-600' />
              </button>
              <button className='p-2 hover:bg-gray-200 rounded-lg transition-colors'>
                <Trash2 className='h-4 w-4 text-gray-600' />
              </button>
            </div>
          </div>
          <p className='text-xs text-gray-600 text-start'>
            Two-factor authentication adds a layer of security to your account by requiring more
            than just a password to log in.{' '}
            <a href='#' className='text-orange-500 hover:underline'>
              Learn more
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  )
}
